import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import crypto from 'crypto-browserify';
import moment from 'moment-timezone';

import OAuth from 'data/OAuth';
import SwaggerAPI from 'data/SwaggerAPI';
import { useBasePath, useSettings, useOrganizations, useEvents, useHealthIntervals, useDevices } from 'hooks';
import { ConfigContext } from './ConfigContext';
import _ from 'lodash';
import { Roles } from 'utils/authHelpers';
import { TimePeriods } from 'utils/ReadingsHelpers';

const AuthContext = React.createContext();

// This performs a raw encoding
function base64URLEncode(str) {
  return str.toString('base64').replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

function sha256(buffer) {
  return crypto.createHash('sha256').update(buffer).digest();
}

const AuthProvider = ({ children }) => {
  const [verifier] = useState(crypto.randomBytes(32));
  const [authCode, setAuthCode] = useState();
  const [reqToken, setReqToken] = useState();
  const [authError, setAuthError] = useState();
  const [loginPage, setLoginPage] = useState(false);
  const [authToken, setAuthTokenState] = useState();
  const [userPrincipal, setUserPrincipalState] = useState();
  const [sessionLoading, setSessionLoading] = useState(true);

  const { setOrg, setCurrentUserPermissions, fetchInstancePermissions, fetchInstanceRoles, setCurrentUserRoles } =
    useSettings();
  const { fetchOrganizations, setSelectedOrganization } = useOrganizations();
  const { setFilter, fetchEvents } = useEvents();
  const { fetchDevices } = useDevices();
  const { requestHealthIntervals, requestSuggestions } = useHealthIntervals();

  const dispatch = useDispatch();
  const history = useHistory();
  const { client_id, audience } = useContext(ConfigContext);
  const { p } = useBasePath();

  const setUserPrincipal = (user) => {
    if (!authError) {
      setUserPrincipalState(user);
      setOrg(user.organization);
      setCurrentUserPermissions(user.permissions);
      fetchInstancePermissions();
      fetchInstanceRoles();
      setCurrentUserRoles(user.roles);
      const fortyEightHoursAgo = new Date(TimePeriods.TwoDaysSeconds).toISOString(); // 48 hours ago - 48 hours * 60 minutes * 60 seconds * 1000 milliseconds

      if (_.includes(user.roles, Roles.Super)) {
        fetchOrganizations();
        setSelectedOrganization();
        fetchEvents({ time_start: fortyEightHoursAgo, time_end: new Date().toISOString() });
        fetchDevices();
      } else {
        setSelectedOrganization(user.organization?.id);
        setFilter({ organization_id: user.organization?.id });
        fetchDevices(user.organization?.id);
        fetchEvents({
          time_start: fortyEightHoursAgo,
          time_end: new Date().toISOString(),
          organization_id: user.organization?.id,
        });
      }
      requestHealthIntervals();
      requestSuggestions();
    }
  };

  useEffect(() => {
    if (client_id) {
      OAuth.initialize(client_id, audience);
    }
  }, [client_id]); // eslint-disable-line

  useEffect(() => {
    if (!client_id) {
      return;
    }
    if (verifier && !reqToken && !authToken && !authError) {
      setSessionLoading(true);
      OAuth.oauthAuthorize({
        app_uri: p('/login'),
        redirect_uri: '/',
        code_challenge: base64URLEncode(sha256(base64URLEncode(verifier))),
      })
        .then((response) => {
          if (response.token) {
            setReqToken(response.token);
            setAuthError(response.error);
            setSessionLoading(false);
          }
          if (response.authCode) {
            setAuthCode(response.authCode);
            const code_verifier = base64URLEncode(base64URLEncode(verifier)); // eslint-disable-line
            const refreshVerifier = crypto.randomBytes(32);
            const newVerifier = crypto.randomBytes(32);
            OAuth.oauthTokenGet({
              code: response.authCode,
              code_verifier,
              refresh_verifier: base64URLEncode(refreshVerifier),
              refresh_nonce: base64URLEncode(sha256(base64URLEncode(newVerifier))), // get refresh verifier - generate at first
            })
              .then((tokenResp) => {
                if (tokenResp && tokenResp.token && tokenResp.token.id_token) {
                  setAuthToken(tokenResp.token, newVerifier);
                  return OAuth.userPrincipal(tokenResp.token.access_token);
                } else {
                  if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
                    console.log('Atomic Session, invalid token', tokenResp.token);
                  }
                  setAuthError('invalid_token');
                }
              })
              .then((user) => {
                console.log('fired use effect set user principal');
                if (user) {
                  setUserPrincipal(user); // It seems like it has to do with this line
                }
                setSessionLoading(false);
              })
              .catch((error) => {
                setSessionLoading(false);
                setAuthError('Invalid Permissions');
                if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
                  console.log('Auth Error Catch', error);
                }
              });
          }
        })
        .catch(() => {
          if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
            console.log('Caught Auth Error');
          }
          setAuthError('Error Contacting Auth Server');
          setSessionLoading(false);
        });
    } else {
      setSessionLoading(false);
    }
  }, [verifier, reqToken, authToken, client_id]); // eslint-disable-line

  // Returns false if the session was NOT refreshed
  const refreshSession = async (token, refreshVerifier) => {
    if (token === undefined) {
      return false;
    }
    if (moment().unix() > parseJwt(token.access_token).exp) {
      const newVerifier = crypto.randomBytes(32);
      try {
        const tokenResp = await OAuth.oauthTokenRefresh({
          refresh_token: token.refresh_token,
          refresh_verifier: base64URLEncode(refreshVerifier),
          refresh_nonce: base64URLEncode(sha256(base64URLEncode(newVerifier))), // get refresh verifier - generate at first
        });
        if (tokenResp) {
          setAuthToken(tokenResp.token, newVerifier);
          if (tokenResp.error !== undefined) {
            if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
              console.log('Refresh Token Expired');
            }
            logout();
            return false;
          }
          return { token: tokenResp.token, refreshVerifier: newVerifier };
        }
      } catch (e) {
        if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
          console.log('Exception with Refresh', e);
        }
        logout();
        return false;
      }
    }
    return false;
  };
  const setLoggingIn = async (loggingInPage) => {
    setLoginPage(loggingInPage);
    if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
      console.log('login Page: ', loginPage);
    }
  };
  const setAuthToken = async (token, refreshVerifier) => {
    SwaggerAPI.initialize(token, refreshSession, refreshVerifier); // eslint-disable-line
    setAuthTokenState(token);
  };

  const parseJwt = (token) => {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => {
          return `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`;
        })
        .join('')
    );

    return JSON.parse(jsonPayload);
  };

  const login = async (username, password) => {
    try {
      const tokenResponse = await OAuth.oauthAuthorize({
        app_uri: p('/login'),
        redirect_uri: '/',
        code_challenge: base64URLEncode(sha256(base64URLEncode(verifier))),
      });
      let _reqToken = reqToken;
      if (tokenResponse.token) {
        _reqToken = tokenResponse.token;
        setReqToken(tokenResponse.token);
      } else if (tokenResponse.error) {
        setAuthError(tokenResponse.error);
        return { error: tokenResponse.error };
      }

      if (_reqToken) {
        const code_verifier = base64URLEncode(verifier); // eslint-disable-line
        const response = await OAuth.login({
          request_token: _reqToken,
          login: username,
          password,
          code_verifier,
          login_uri: p('/'),
        });
        if (response.authCode) {
          setAuthCode(response.authCode);
          setAuthError(response.error);
          if (response.error === null) {
            // const currentVerifier = refreshVerifier;
            const newVerifier = crypto.randomBytes(32);
            const tokenResp = await OAuth.oauthTokenGet({
              code: response.authCode,
              code_verifier,
              refresh_verifier: base64URLEncode(newVerifier),
              refresh_nonce: base64URLEncode(sha256(base64URLEncode(newVerifier))), // get refresh verifier - generate at first
            });
            if (tokenResp && tokenResp.token) {
              setAuthToken(tokenResp.token, newVerifier);
            } else {
              return { error: 'invalid_token' };
            }
            console.log('fired login set user principal');
            const user = await OAuth.userPrincipal(tokenResp.token.access_token);
            if (user) {
              setUserPrincipal(user);
            }
          }
        }
        return response;
      }
      if (authCode) {
        return { authCode };
      }
    } catch (error) {
      setSessionLoading(false);
      setAuthError(error.statusText);
      if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
        console.log('Auth Error Catch', error);
      }
      return { error: error.statusText };
    }
    return { error: 'failed' };
  };

  const logout = async () => {
    await OAuth.logout(p('/'));
    setAuthCode();
    setAuthError();
    setReqToken();
    setAuthToken();
    history.push(p('/login'));
    dispatch({ type: 'LOGOUT_USER' });
  };

  return (
    <AuthContext.Provider
      value={{
        login,
        logout,
        setAuthError,
        setLoggingIn,
        authCode,
        authError,
        sessionValid: !!authToken,
        accessToken: authToken && authToken.access_token,
        sessionLoading,
        user: userPrincipal,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { AuthProvider, AuthContext };
